import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Code2,
  Database,
  Download,
  Eye,
  Globe,
  MoreHorizontal,
  BookOpen,
  Rocket,
  Settings2,
  UsersRound
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { downloadProjectZipApi } from '../../api/agent.api';
import { useI18n } from '../../hooks/useI18n';
import { classNames } from '../../utils/classNames';
import { Button } from '../ui/Button';

const iconButtonClass =
  'inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-xs font-medium text-neutral-600 transition hover:bg-neutral-100 hover:text-neutral-900 dark:text-muted dark:hover:bg-accent-soft dark:hover:text-text';

const activeIconButtonClass =
  'bg-neutral-100 text-neutral-900 dark:bg-accent-soft dark:text-text';

export const WorkspaceTopBar = ({
  project,
  backTo = '/projects',
  backAriaLabel,
  sectionLabel,
  activeSection,
  onPublish,
  onDownload,
  onRecentDeployments,
  onUsers,
  onDatabase,
  onSkills,
  onSettings,
  authEnabled = false,
  databaseEnabled = false,
  panelMode = 'preview',
  onPanelModeChange
}) => {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const BackIcon = language === 'fa' ? ArrowRight : ArrowLeft;
  const projectId = project?.id;
  const workspacePath = projectId ? `/workspace/${projectId}` : '/projects';
  const showPanelSwitch = typeof onPanelModeChange === 'function';

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!moreRef.current?.contains(event.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const goUsers = () => {
    if (onUsers) {
      onUsers();
      return;
    }
    if (projectId) {
      navigate(`${workspacePath}/users`);
    }
  };

  const goDatabase = () => {
    if (onDatabase) {
      onDatabase();
      return;
    }
    if (projectId) {
      navigate(`${workspacePath}/requests`);
    }
  };

  const goSkills = () => {
    if (onSkills) {
      onSkills();
      return;
    }
    if (projectId) {
      navigate(`${workspacePath}/skills`);
    }
  };

  const goPublish = () => {
    if (onPublish) {
      onPublish();
      return;
    }
    if (projectId) {
      navigate(`${workspacePath}/deployments`);
    }
  };

  const goSettings = () => {
    if (onSettings) {
      onSettings();
      return;
    }
    if (projectId) {
      navigate(`/projects/${projectId}/edit`, {
        state: { returnTo: workspacePath }
      });
    }
  };

  const goDownload = async () => {
    if (onDownload) {
      onDownload();
      return;
    }
    if (!projectId) {
      return;
    }
    try {
      const blob = await downloadProjectZipApi(projectId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${project?.name || projectId}.zip`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.warn(error);
    }
  };

  const moreItems = [
    {
      key: 'recent-deployments',
      label: t('recentDeployments'),
      icon: Rocket,
      onClick: onRecentDeployments || goPublish,
      active: activeSection === 'publish'
    },
    {
      key: 'download',
      label: t('downloadProject'),
      icon: Download,
      onClick: goDownload
    },
    {
      key: 'settings',
      label: t('projectSettings'),
      icon: Settings2,
      onClick: goSettings,
      active: activeSection === 'settings'
    }
  ];

  return (
    <header className="relative z-40 flex h-12 shrink-0 items-center gap-2 border-b border-neutral-200 bg-white px-2 sm:px-3 dark:border-border dark:bg-panel-strong">
      <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
        <Link
          to={backTo}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:text-muted dark:hover:bg-accent-soft dark:hover:text-text"
          aria-label={backAriaLabel || t('backToProjects')}
        >
          <BackIcon size={16} />
        </Link>

        <div className="ms-1 flex min-w-0 items-center gap-2">
          <h1 className="truncate text-sm font-semibold tracking-tight text-neutral-950 dark:text-text">
            {project?.name || t('workspace')}
          </h1>
          {sectionLabel ? (
            <>
              <span className="shrink-0 text-neutral-300 dark:text-white/20">/</span>
              <span className="min-w-0 truncate text-sm font-medium text-neutral-500 dark:text-muted">{sectionLabel}</span>
            </>
          ) : null}
        </div>
      </div>

      {showPanelSwitch ? (
        <div className="flex items-center gap-0.5">
          {panelMode === 'preview' ? (
            <button
              type="button"
              onClick={() => onPanelModeChange?.('editor')}
              className={iconButtonClass}
            >
              <Code2 size={15} />
              <span className="hidden lg:inline">{t('editorTab')}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onPanelModeChange?.('preview')}
              className={iconButtonClass}
            >
              <Eye size={15} />
              <span className="hidden lg:inline">{t('previewTab')}</span>
            </button>
          )}
        </div>
      ) : null}

      <div className="flex shrink-0 items-center gap-1 sm:gap-1.5">
        <button
          type="button"
          onClick={goDatabase}
          className={classNames(
            iconButtonClass,
            !databaseEnabled && 'opacity-70',
            activeSection === 'database' && activeIconButtonClass
          )}
          aria-label={t('databasePanel')}
          aria-current={activeSection === 'database' ? 'page' : undefined}
        >
          <Database size={15} />
          <span className="hidden lg:inline">{t('databasePanel')}</span>
        </button>

        <button
          type="button"
          onClick={goUsers}
          className={classNames(
            iconButtonClass,
            !authEnabled && 'opacity-70',
            activeSection === 'users' && activeIconButtonClass
          )}
          aria-label={t('usersPanel')}
          aria-current={activeSection === 'users' ? 'page' : undefined}
        >
          <UsersRound size={15} />
          <span className="hidden lg:inline">{t('usersPanel')}</span>
        </button>

        <button
          type="button"
          onClick={goSkills}
          className={classNames(iconButtonClass, activeSection === 'skills' && activeIconButtonClass)}
          aria-label={t('skillsPanel')}
          aria-current={activeSection === 'skills' ? 'page' : undefined}
        >
          <BookOpen size={15} />
          <span className="hidden lg:inline">{t('skillsPanel')}</span>
        </button>

        <div ref={moreRef} className="relative">
          <button
            type="button"
            onClick={() => setMoreOpen((value) => !value)}
            aria-label={t('more')}
            aria-expanded={moreOpen}
            className={classNames(
              'inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:text-muted dark:hover:bg-accent-soft dark:hover:text-text',
              (moreOpen || activeSection === 'settings') && activeIconButtonClass
            )}
          >
            <MoreHorizontal size={15} />
          </button>
          {moreOpen ? (
            <div className="absolute end-0 top-full z-50 mt-1 min-w-[200px] overflow-hidden rounded-xl border border-neutral-200 bg-[#F3F4F6] py-1 shadow-[0_12px_40px_rgba(0,0,0,0.08)] dark:border-border dark:bg-panel dark:shadow-[0_16px_40px_rgba(0,0,0,0.4)]">
              {moreItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => {
                      setMoreOpen(false);
                      item.onClick?.();
                    }}
                    className={classNames(
                      'flex w-full items-center gap-2.5 px-3.5 py-2.5 text-start text-sm text-neutral-700 transition hover:bg-white dark:text-text dark:hover:bg-accent-soft',
                      item.active && 'bg-white font-medium text-neutral-950 dark:bg-accent-soft dark:text-text'
                    )}
                  >
                    <Icon size={15} className="shrink-0 opacity-80" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        <Button
          type="button"
          variant={activeSection === 'publish' ? 'secondary' : 'primary'}
          className="ms-1 h-8 rounded-lg px-3 text-xs"
          onClick={goPublish}
          aria-current={activeSection === 'publish' ? 'page' : undefined}
        >
          <Globe size={14} />
          <span className="hidden sm:inline">{t('publish')}</span>
        </Button>
      </div>
    </header>
  );
};
